import Cookies from "js-cookie";

const COOKIE_NAME = "runner_app";
const COOKIE_OPTS = { path: "/", sameSite: "Lax" };

/** Used only when a token carries no exp claim; Cognito issues 60-minute tokens. */
const FALLBACK_TTL_MS = 60 * 60 * 1000;

const LAST_TOKEN_AT_KEY = "runner_last_token_at";

/** Decode JWT payload (no signature verification; server is source of truth). */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isJwtExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const expSec =
    typeof payload.exp === "number"
      ? payload.exp
      : typeof payload.expires === "number"
        ? payload.expires
        : null;
  if (expSec == null) return false;
  return expSec * 1000 <= Date.now();
}

/**
 * Store the session token, expiring the cookie exactly when the token does.
 *
 * The cookie used to live for 7 days while the token inside it lasted an hour, so a
 * browser kept presenting a dead credential — and, because nothing re-checks that the
 * user still exists in Cognito, a deleted account stayed usable for the remainder of
 * that window. Matching the two means the session ends when the token does.
 */
export function setTokenCookie(idToken) {
  const payload = decodeJwtPayload(idToken);
  const expSec = typeof payload?.exp === "number" ? payload.exp : null;
  const expires = expSec
    ? new Date(expSec * 1000)
    : new Date(Date.now() + FALLBACK_TTL_MS);
  Cookies.set(COOKIE_NAME, idToken, {
    ...COOKIE_OPTS,
    expires,
    // Local development is served over http, where a secure cookie is dropped.
    secure: window.location.protocol === "https:",
  });
  recordTokenIssued();
}

function recordTokenIssued() {
  try {
    sessionStorage.setItem(LAST_TOKEN_AT_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

export function clearTokenSession() {
  Cookies.remove(COOKIE_NAME, { path: "/" });
  try {
    sessionStorage.removeItem(LAST_TOKEN_AT_KEY);
  } catch { /* ignore */ }
}

/**
 * With Cognito, there's no custom renew-token endpoint.
 * The access token is valid for its lifetime (default 1 hour).
 * Returns true if a valid token exists, false otherwise.
 */
export async function renewTokenIfStale() {
  const token = Cookies.get(COOKIE_NAME);
  if (!token) return false;
  if (isJwtExpired(token)) {
    clearTokenSession();
    return false;
  }
  return true;
}

export function getCookieToken() {
  return Cookies.get(COOKIE_NAME);
}
